import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AssessmentOverviewAdapter } from 'components/Assessments/SocAssessment/sections/AssessmentOverview/adapter'
import { PatientOverviewAdapter } from 'components/Assessments/SocAssessment/sections/PatientOverview/adapter'
import { MedicalBackgroundAdapter } from 'components/Assessments/SocAssessment/sections/MedicalBackground/adapter'
import { MedicationsAdapter } from 'components/Assessments/SocAssessment/sections/Medications/adapter'
import { SkilledNeedsAdapter } from 'components/Assessments/SocAssessment/sections/SkilledNeeds/adapter'
import { ADLNeedsAdapter } from 'components/Assessments/SocAssessment/sections/ADLNeeds/adapter'
import { DischargePlanningAdapter } from 'components/Assessments/SocAssessment/sections/DischargePlanning/adapter'
import { AuthorizationAdapter } from 'components/Assessments/SocAssessment/sections/Authorization/adapter'
import { VisitSchedulingAdapter } from 'components/Assessments/SocAssessment/sections/VisitScheduling/adapter'
import { PlanOfCareAdapter } from 'components/Assessments/SocAssessment/sections/PlanOfCare/adapter'
import { SupervisoryReviewAdapter } from 'components/Assessments/SocAssessment/sections/SupervisoryReview/adapter'
import { NarrativeAdapter } from 'components/Assessments/SocAssessment/sections/Narrative/adapter'
import { FinalStepsAdapter } from 'components/Assessments/SocAssessment/sections/FinalSteps/adapter'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { BaseSubsectionAdapter } from '../adapters/BaseSubsectionAdapter'
import { BaseAssessmentAdapter } from '../adapters/BaseAssessmentAdapter'
import { AdapterOptions, SectionAdapter } from '../adapters/types'
import { AssessmentType } from '../../Clinical/Assessments/constants'
import { mutateFlatAnswers } from '../helpers'

/**
 * Array of all SOC section adapter instances
 */
const SOC_SECTION_ADAPTERS: BaseSubsectionAdapter<SocFormData>[] | SectionAdapter<SocFormData>[] = [
  new AssessmentOverviewAdapter(),
  new PatientOverviewAdapter(),
  new MedicalBackgroundAdapter(),
  new MedicationsAdapter(),
  new SkilledNeedsAdapter(),
  new ADLNeedsAdapter(),
  new DischargePlanningAdapter(),
  new AuthorizationAdapter(),
  new VisitSchedulingAdapter(),
  new PlanOfCareAdapter(),
  new SupervisoryReviewAdapter(),
  new NarrativeAdapter(),
  new FinalStepsAdapter()
]

export class SocAssessmentAdapter extends BaseAssessmentAdapter<SocFormData> {
  readonly assessmentType = AssessmentType.SOC

  protected flatAnswersToFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): SocFormData {
    const formData: Partial<SocFormData> = {}

    for (const adapter of SOC_SECTION_ADAPTERS) {
      const sectionData = adapter.extractFormData(flatAnswers, options)
      Object.assign(formData, sectionData)
    }

    return formData as SocFormData
  }

  protected formDataToFlatAnswers(
    formData: SocFormData,
    currentFlatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Record<string, QuestionnaireResponseItem> {
    // Use composable section adapters for clean, maintainable transformation
    const allMutations: Array<{ linkId: string; value: unknown }> = []

    // Build mutations from all section adapters dynamically
    for (const adapter of SOC_SECTION_ADAPTERS) {
      if (adapter instanceof BaseSubsectionAdapter) {
        adapter.setFormData(formData)
        allMutations.push(...adapter.buildFlatAnswers())
      } else {
        allMutations.push(...adapter.buildFlatAnswers(formData))
      }
    }

    // Apply all mutations using existing helper
    mutateFlatAnswers(currentFlatAnswers, allMutations)

    return currentFlatAnswers
  }
}
